import React, { useRef } from 'react'
import styled, { css } from 'styled-components'
import {
  AriaDialogProps,
  DismissButton,
  mergeProps,
  useOverlay,
  useOverlayPosition,
  useOverlayTrigger,
  usePreventScroll,
} from 'react-aria'
import { useOverlayTriggerState } from 'react-stately'
import { ThemeProviderClean } from '../../../../ThemeProviderClean'
import { RootPortal } from '../../../core-components'
import { useFocusVisible } from '../../hooks'
import { AriaProps } from '../../types'
import { useElementScrolledOut } from '../../../internal-helpers/useElementScrolledOut'
import { SafeFocusScope } from '../../../internal-helpers/SafeFocusScope'
import { useAriaHideOutside } from '../../../helpers/useAriaHideOutside'

interface Props {
  children: React.ReactNode
  dialogAriaProps?: AriaDialogProps
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  trigger: (
    triggerProps: AriaProps & {
      popoverRef: React.RefObject<HTMLDivElement | null>
      positionRef: React.RefObject<HTMLDivElement | null>
    }
  ) => React.ReactNode
}

export const FieldPopover = ({
  trigger,
  children,
  isOpen,
  onOpenChange,
  dialogAriaProps,
}: Props) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const positionRef = useRef<HTMLDivElement>(null)
  const state = useOverlayTriggerState({ isOpen, onOpenChange })
  const { overlayProps: overlayPositionProps } = useOverlayPosition({
    overlayRef: popoverRef,
    targetRef: positionRef,
    placement: 'bottom start',
    isOpen: state.isOpen,
    offset: 8,
  })
  const { triggerProps, overlayProps } = useOverlayTrigger({ type: 'dialog' }, state, positionRef)
  usePreventScroll()
  return (
    <>
      {trigger({ ...triggerProps, elementRef: triggerRef, positionRef, popoverRef })}
      <RootPortal>
        {state.isOpen && (
          <ThemeProviderClean>
            <PopoverOverlay
              {...mergeProps(overlayProps, overlayPositionProps, dialogAriaProps)}
              onClose={state.close}
              popoverRef={popoverRef}
              triggerRef={triggerRef}
            >
              {children}
            </PopoverOverlay>
          </ThemeProviderClean>
        )}
      </RootPortal>
    </>
  )
}

interface PopoverContentProps extends React.DOMAttributes<HTMLDivElement> {
  children: React.ReactNode
  onClose: () => void
  popoverRef: React.RefObject<HTMLDivElement | null>
  triggerRef: React.RefObject<HTMLButtonElement | null>
}

const PopoverOverlay = ({
  onClose,
  popoverRef,
  children,
  triggerRef,
  ...otherProps
}: PopoverContentProps) => {
  const { overlayProps } = useOverlay(
    {
      isOpen: true,
      onClose,
      isDismissable: true,
    },
    popoverRef
  )
  const { focusProps, isFocusVisible } = useFocusVisible()

  useElementScrolledOut(triggerRef, onClose)
  useAriaHideOutside([popoverRef])

  return (
    <SafeFocusScope restoreFocus contain>
      <StyledWrapper
        isFocusVisible={isFocusVisible}
        ref={popoverRef}
        {...mergeProps({ role: 'dialog' }, otherProps, overlayProps, focusProps)}
      >
        {children}
      </StyledWrapper>
      <DismissButton onDismiss={onClose} />
    </SafeFocusScope>
  )
}

const StyledWrapper = styled.div<{ isFocusVisible: boolean }>`
  ${({ isFocusVisible }) =>
    !isFocusVisible &&
    css`
      :focus {
        outline: none;
      }
    `}
`
